import { ArrowForward as ArrowForwardIcon, CheckCircle as CheckIcon } from '@mui/icons-material';
import { Box, Button, Chip, Container, Grid, Stack, Typography } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React from 'react';

const BG = '#0a0a0a';
const BG_CARD = '#111111';
const BG_SURFACE = '#161616';
const ELECTRIC_BLUE = '#2196f3';
const PURPLE = '#9c27b0';
const ORANGE = '#ff9800';
const GREEN = '#00e676';
const TEXT = '#e5e5e5';
const TEXT_DIM = '#8f8f8f';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: { default: BG, paper: BG_CARD },
    primary: { main: ELECTRIC_BLUE },
    secondary: { main: PURPLE },
    text: { primary: TEXT, secondary: TEXT_DIM },
  },
  typography: { fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif' },
});

const categories = [
  'Cloud — backup, platforms, SaaS',
  'Connectivity — fiber, coax, wireless',
  'Security — compliance, assessments, managed security',
  'Managed Services — IT support, monitoring, helpdesk',
  'Voice — SIP, PRI, UCaaS',
  'AI Services — AI operations, automation',
  'Consulting — strategy, branding, implementation',
];

function Section({ children, sx }: { children: React.ReactNode; sx?: object }) {
  return (
    <Box component="section" sx={{ py: { xs: 8, md: 12 }, ...sx }}>
      <Container maxWidth="lg">{children}</Container>
    </Box>
  );
}

export default function ForBuyersPage() {
  const router = useRouter();

  return (
    <ThemeProvider theme={darkTheme}>
      <Head>
        <title>Aithon — Compare Business Services. See Real Pricing.</title>
        <meta name="description" content="Stop shopping blind for business services. Compare providers, see real pricing, and get visible perks — no forms, no phone calls, no brokers." />
        <link rel="canonical" href="https://aithon.tech/for-buyers" />
        <meta property="og:title" content="Aithon — Compare Business Services. See Real Pricing." />
        <meta property="og:description" content="Compare providers, see real pricing, and get visible perks for business services without phone-tag or broker opacity." />
      </Head>
      <Box sx={{ bgcolor: BG, minHeight: '100vh', color: TEXT }}>
        <Box sx={{ pt: { xs: 12, md: 18 }, pb: { xs: 8, md: 14 }, background: 'radial-gradient(ellipse at 25% 20%, rgba(33,150,243,0.08) 0%, transparent 50%), radial-gradient(ellipse at 80% 70%, rgba(0,230,118,0.06) 0%, transparent 50%)' }}>
          <Container maxWidth="lg">
            <Chip label="FOR BUYERS" sx={{ mb: 3, bgcolor: 'rgba(0,230,118,0.1)', color: GREEN, border: '1px solid rgba(0,230,118,0.3)', fontWeight: 700 }} />
            <Typography variant="h1" sx={{ fontWeight: 900, fontSize: { xs: '2.5rem', md: '4.3rem' }, lineHeight: 1.08, mb: 3, maxWidth: 820 }}>
              Stop shopping blind for business services.
            </Typography>
            <Typography sx={{ color: TEXT_DIM, fontSize: { xs: '1.08rem', md: '1.28rem' }, lineHeight: 1.7, maxWidth: 860, mb: 5 }}>
              Every other market in your life shows you the price before you commit. Cloud, connectivity,
              security, managed IT, and voice services should be no different.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button variant="contained" size="large" endIcon={<ArrowForwardIcon />} onClick={() => router.push('/')}>
                Browse Services
              </Button>
              <Button variant="outlined" size="large" onClick={() => router.push('/how-aithon-works')} sx={{ color: TEXT, borderColor: 'rgba(255,255,255,0.2)' }}>
                See How Aithon Works
              </Button>
            </Stack>
          </Container>
        </Box>

        <Section>
          <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '1.9rem', md: '2.6rem' }, mb: 4 }}>
            What you get on Aithon
          </Typography>
          <Grid container spacing={3}>
            {[
              ['Real pricing', 'Every listing shows the actual price. No “contact us for a quote.” No forms. No phone calls.'],
              ['Side-by-side comparison', 'Same service, multiple providers, scored and ranked by price, perks, trust score, and SLA performance.'],
              ['Visible perks', 'Providers compete with real incentives like audits, setup, bill credits, and hardware bundles.'],
              ['Transparent ranking', 'Providers are ranked by Perk Value Score, Trust Score, and SLA compliance — not by who paid for placement.'],
            ].map(([title, desc], index) => (
              <Grid item xs={12} md={6} key={title}>
                <Box sx={{ p: 3.5, borderRadius: 3, bgcolor: index % 2 === 0 ? BG_CARD : BG_SURFACE, border: '1px solid rgba(255,255,255,0.06)', height: '100%' }}>
                  <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.15rem', mb: 1 }}>{title}</Typography>
                  <Typography sx={{ color: TEXT_DIM, lineHeight: 1.75 }}>{desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Section>

        <Section sx={{ bgcolor: BG_SURFACE }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '1.9rem', md: '2.4rem' }, mb: 3 }}>
                How the old way works vs. how Aithon works
              </Typography>
              <Box sx={{ p: 3, borderRadius: 3, bgcolor: BG_CARD, border: '1px solid rgba(255,255,255,0.06)', mb: 3 }}>
                <Typography sx={{ color: ORANGE, fontWeight: 700, mb: 1.5 }}>OLD WAY</Typography>
                <Stack spacing={1.25}>
                  {[
                    'Google the category and location',
                    'Fill out multiple forms',
                    'Wait for sales calls',
                    'Get different prices for the same thing',
                    'Pick whoever was least annoying',
                    'Still wonder if you got a good deal',
                  ].map((item) => (
                    <Stack key={item} direction="row" spacing={1.25}><Typography sx={{ color: ORANGE }}>•</Typography><Typography sx={{ color: TEXT_DIM }}>{item}</Typography></Stack>
                  ))}
                </Stack>
              </Box>
              <Box sx={{ p: 3, borderRadius: 3, bgcolor: 'rgba(33,150,243,0.08)', border: '1px solid rgba(33,150,243,0.28)' }}>
                <Typography sx={{ color: ELECTRIC_BLUE, fontWeight: 700, mb: 1.5 }}>AITHON</Typography>
                <Stack spacing={1.25}>
                  {[
                    'Search once',
                    'See all providers, real prices, and visible perks',
                    'Compare side by side',
                    'Pick the best offer',
                    'Know the market showed you the best deal',
                  ].map((item) => (
                    <Stack key={item} direction="row" spacing={1.25} alignItems="flex-start"><CheckIcon sx={{ color: GREEN, fontSize: 18, mt: '3px' }} /><Typography sx={{ color: TEXT }}>{item}</Typography></Stack>
                  ))}
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '1.9rem', md: '2.4rem' }, mb: 3 }}>
                Categories available now
              </Typography>
              <Stack spacing={1.6}>
                {categories.map((item) => (
                  <Box key={item} sx={{ p: 2.2, borderRadius: 2.5, bgcolor: BG_CARD, border: '1px solid rgba(255,255,255,0.06)' }}>
                    <Typography sx={{ color: TEXT }}>{item}</Typography>
                  </Box>
                ))}
              </Stack>
              <Typography sx={{ color: TEXT_DIM, mt: 2.5 }}>43 services live and growing.</Typography>
            </Grid>
          </Grid>
        </Section>
      </Box>
    </ThemeProvider>
  );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  context.res.setHeader('Cache-Control', 'public, s-maxage=3600, stale-while-revalidate=7200');
  return { props: {} };
};
